import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { initAppBridge, setTitle } from './shopify';
import { bootstrapSession } from './api';
import { router, ROUTES, type Route, type RouteName } from './router';

// Page components — import so they self-register as custom elements.
import './pages/dashboard-page';
import './pages/countdown-page';
import './pages/sales-popup-page';
import './pages/stock-counter-page';
import './pages/announcement-bar-page';
import './pages/visitor-counter-page';
import './pages/analytics-page';
import './pages/billing-page';

@customElement('hype-app')
export class HypeApp extends LitElement {
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _route: Route = router.current;

  private _unsubRouter?: () => void;
  private _navHandler?: (e: Event) => void;

  override createRenderRoot() { return this; }

  async connectedCallback() {
    super.connectedCallback();

    try {
      await initAppBridge();
      await bootstrapSession();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Failed to load configuration';
      this._loading = false;
      return;
    }

    // Set up App Bridge NavigationMenu for embedded app sidebar.
    // This tells Shopify admin what nav items to show.
    this._setupNavigation();

    // Intercept shopify:navigate events fired by Polaris s-link components.
    // Prevent Polaris's default full-page navigation and route through our
    // History API router instead. App Bridge auto-syncs admin URL when
    // pushState is called inside navigate().
    this._navHandler = (e: Event) => {
      e.preventDefault();
      const href = (e.target as HTMLElement)?.getAttribute?.('href') ?? '/';
      const matchedRoute = ROUTES.find(r => r.path === href);
      router.navigate(matchedRoute ? matchedRoute.name : 'dashboard');
    };
    document.addEventListener('shopify:navigate', this._navHandler);

    this._unsubRouter = router.subscribe(route => {
      this._route = route;
      setTitle(route.title);
    });

    this._loading = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubRouter?.();
    if (this._navHandler) {
      document.removeEventListener('shopify:navigate', this._navHandler);
      this._navHandler = undefined;
    }
  }

  /**
   * Configure the App Bridge NavigationMenu.
   * In the CDN version, we use window.shopify to set navigation items
   * that appear in the Shopify admin sidebar for our app.
   */
  private _setupNavigation(): void {
    // For CDN App Bridge, navigation is configured in shopify.app.toml
    // and the admin handles it natively. No runtime config needed.
    // The path router handles the actual page switching.
  }

  private _renderPage(name: RouteName) {
    switch (name) {
      case 'countdown':        return html`<hype-countdown-page></hype-countdown-page>`;
      case 'sales-popup':      return html`<hype-sales-popup-page></hype-sales-popup-page>`;
      case 'stock-counter':    return html`<hype-stock-counter-page></hype-stock-counter-page>`;
      case 'announcement-bar': return html`<hype-announcement-bar-page></hype-announcement-bar-page>`;
      case 'visitor-counter':  return html`<hype-visitor-counter-page></hype-visitor-counter-page>`;
      case 'analytics':        return html`<hype-analytics-page></hype-analytics-page>`;
      case 'billing':          return html`<hype-billing-page></hype-billing-page>`;
      case 'dashboard':
      default:                 return html`<hype-dashboard-page></hype-dashboard-page>`;
    }
  }

  render() {
    if (this._loading) {
      return html`<s-spinner></s-spinner>`;
    }

    if (this._error) {
      return html`
        <s-page title="Error">
          <s-banner tone="critical">
            <p>${this._error}</p>
          </s-banner>
        </s-page>
      `;
    }

    return this._renderPage(this._route.name);
  }
}
